import { ConnectionType } from '@sembo-private/maps';
import { defineStore } from 'pinia';
import { computed, watch, ref } from 'vue';

import {
  ActivityIngredient,
  HotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';
import type { StayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  isActivityIngredient,
  isFerryIngredient,
  isFlightIngredient,
  isOwnStayIngredient,
  isStayIngredient,
  isTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  MapStateKey,
  RecipeStateKey,
  TripStateKey,
} from '@apps/main/modules/state/state';
import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import { useItineraryStore } from '@apps/main/modules/store/itineraryStore';
import type { TripOverviewInitiatedMetadata } from '@apps/main/new-desktop/core/models';
import {
  CustomerOriginQueryParamValue,
  TripPlannerPage,
} from '@apps/main/new-desktop/core/models';
import {
  useBrowserStore,
  useGeolocationStore,
  useRouterStore,
  useStatsigEventsStore,
  useUserLocationStore,
  usePriceCalendarStore,
  useQueryParamsStore,
} from '@apps/main/new-desktop/core/stores';
import type {
  AddFlightsModalInitialRoute,
  AddFlightsModalRouteStopData,
  AddFligthsModalRoute,
} from '@apps/main/new-desktop/modules/addFlightsModal';
import { usePreferredTimeStore } from '@apps/main/modules/store/preferredTimeStore';
import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { readonlyRef } from '@apps/main/modules/state/enux';

import { trackNightsChanged } from '@libs/google-analytics-events';
import type {
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  HeartedHotel,
  IngredientId,
  PortInfo,
} from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrder,
  MyPositionType,
  isBookableTransportType,
  TransportType,
} from '@libs/trip-planner-interfaces';
import {
  assertValue,
  exhaustiveGuard,
  injectStrict,
  isBookableTransport,
} from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import { isStena } from '@libs/cookie';

import type {
  AlternativeRoomSuggestionAccommodationSegment,
  ItinerartBookableSegmentType,
  ItineraryBookableTransportSegment,
  ItineraryLoaderSegment,
  ItineraryLocationLoaderSegment,
  ItineraryLocationSegment,
  ItineraryNoTransportLoaderSegment,
  ItineraryNoTransportSegment,
  ItinerarySegment,
  ItinerarySimpleTransportSegment,
  ItineraryTransportLoaderSegment,
  UnavailabilityReason,
  TripOverviewSegments,
  ItineraryUpsellNoTransportSegment,
  ItineraryStenaUpsellNoTransportSegment,
  AlternativeTicketSuggestionActivitySegment,
} from '../models';
import {
  ItinerarySegmentType,
  TripOverviewTemplateIds,
  isItineraryBookableTransport,
  isItinerarySimpleTransportSegment,
} from '../models';

import { getTripItineraryLoaderSegments } from './tripOverviewLoaderLogic';
import { useTripOverviewModalsStore } from './tripOverviewModalsStore';
import { getTripOverviewSegments } from './tripOverviewSegments';
import { mapTransportTypeToConnectionType } from './tripOverviewSegments/tripOverviewStoreSegmentsLogic';
import {
  getAutocompleteTypeFromLocationSegment,
  getFerryDetails,
  getFlightDetails,
  getHomeFlightData,
  getSearchTextFromLocationSegment,
  updateFlightIngredientsWithAlternativeSuggestion,
} from './tripOverviewStoreLogic';
import { useUpsellStore } from './upsell/upsellStore';
import { getUnavailableAccommodationMessage } from './tripOverviewSegments/tripOverviewHotelRelatedSegmentsLogic';
import {
  getUnavailableFlightMessage,
  getUnavailableFerryMessage,
} from './tripOverviewSegments/tripOverviewBookableTransportSegmentLogic';
import { getUnavailableActivityMessage } from './tripOverviewSegments/tripOverviewActivitySegmentsLogic';

export const useTripOverviewPageStore = defineStore(
  'TripOverviewPageStore',
  () => {
    const mapState = injectStrict(MapStateKey);
    const tripState = injectStrict(TripStateKey);
    const recipeState = injectStrict(RecipeStateKey);
    const itineraryStore = useItineraryStore();
    const upsellStore = useUpsellStore();
    const routerStore = useRouterStore();
    const tripOverviewModalsStore = useTripOverviewModalsStore();
    const editingIngredientStore = useEditingIngredientStore();
    const geolocationStore = useGeolocationStore();
    const statsigEventsStore = useStatsigEventsStore();
    const preferredTimeStore = usePreferredTimeStore();
    const browserStore = useBrowserStore();
    const userLocationStore = useUserLocationStore();
    const priceCalendarStore = usePriceCalendarStore();
    const queryParamsStore = useQueryParamsStore();

    const isFirstTripItineraryRender =
      routerStore.landingRoute === TripPlannerPage.TripOverview;

    const hasUserSelectedTransport = ref<boolean>(false);

    // Actions
    function initializeView(): void {
      loadUserGeolocationToMapState();
      const metadata: TripOverviewInitiatedMetadata = {
        totalAccomodation: recipeState.hotelOnlyRecipe.value.length,
        totalFerry: recipeState.ferryOnlyRecipe.value.length,
        totalFlight: recipeState.flightOnlyRecipe.value.length,
        totalActivity: recipeState.activityOnlyRecipe.value.length,
      };
      statsigEventsStore.logTripOverviewInitiated(metadata);
    }

    function setUserSelectedTransport(value: boolean): void {
      hasUserSelectedTransport.value = value;
    }

    function deleteIngredientById(ingredientId: IngredientId): void {
      recipeState.deleteIngredientById(ingredientId);
    }

    function changeDestinationNumberOfNights(
      ingredientId: IngredientId,
      numberOfNights: number
    ): void {
      const stayIngredient = recipeState.getStayIngredient(ingredientId);
      if (!stayIngredient) {
        return;
      }
      stayIngredient.numberOfNights = numberOfNights;
      trackNightsChanged();
    }

    function confirmAlternativeFlight(ingredientId: IngredientId): void {
      const flightIngredientResponse =
        itineraryStore.getFlightIngredientResponse(ingredientId);

      const flightResponses =
        itineraryStore.selectedItinerary?.flightOnlyItinerary;

      assertValue(
        flightResponses,
        'tripOverviewPageStore.ts > confirmAlternativeFlight > flightResponses'
      );

      const flightsInPackage = flightResponses.filter(
        (flight) =>
          flight.alternativeSuggestion?.addToBasketToken ===
          flightIngredientResponse?.alternativeSuggestion?.addToBasketToken
      );

      flightsInPackage.forEach((flight) => {
        const alternativeFlight = flight.alternativeSuggestion;

        assertValue(
          alternativeFlight,
          'tripOverviewPageStore.ts > confirmAlternativeFlight > alternativeFlight'
        );

        updateFlightIngredientsWithAlternativeSuggestion(
          flight.ingredientId,
          alternativeFlight,
          recipeState.getFlightIngredient
        );
      });
    }

    function getFromPortInfoFromEditingIngredient(): PortInfo | undefined {
      const ingredient = editingIngredientStore.editingTransportIngredient;
      if (!ingredient) {
        return undefined;
      }
      return {
        code: ingredient.fromPort,
        label: ingredient.fromPortDisplayText,
        name: ingredient.fromPortText,
      };
    }

    function getToPortInfoFromEditingIngredient(): PortInfo | undefined {
      const ingredient = editingIngredientStore.editingTransportIngredient;
      if (!ingredient) {
        return undefined;
      }
      return {
        code: ingredient.toPort,
        label: ingredient.toPortDisplayText,
        name: ingredient.toPortText,
      };
    }

    // Getters

    const numberOfDestinations = computed<number>(
      () => recipeState.stayOnlyRecipe.value.length
    );

    const numberOfNights = computed<number>(() =>
      recipeState.stayOnlyRecipe.value.reduce((acc, hotel) => {
        if (hotel.numberOfNights) {
          acc += hotel.numberOfNights;
        }
        return acc;
      }, 0)
    );

    const hasRecipeIngredients = computed(
      () => recipeState.hasRecipeIngredients.value
    );

    const homeFlightData = computed(() =>
      getHomeFlightData(recipeState.recipe.value)
    );

    const tripItinerary = computed<ItinerarySegment[]>(() => {
      const tripOverviewSegments: TripOverviewSegments = {
        chronologicalItinerary: tripState.chronologicalItinerary.value,
        startLocation: userLocationStore.startLocation,
        endLocation: userLocationStore.endLocation,
        getIngredient: recipeState.getIngredient,
        getIngredientIndex: recipeState.getIngredientIndex,
        paxes: recipeState.pax.value.rooms,
        isSuggestedHotel: tripState.isSuggestedHotel,
        isSuggestedTransport: tripState.isSuggestedTransport,
        endLocationIngredient: recipeState.endLocationIngredient.value,
        calculatedRoute: mapState.calculatedRoute.value,
        ferryTicketCategoryDictionary:
          upsellStore.ferryTicketCategoryDictionary,
        userSelectedFerryTicketCategory:
          upsellStore.userSelectedFerryTicketCategory,
        ferryVehicleTypes: tripState.ferryVehicleTypes.value,
        areThereActivitiesInDestination:
          tripState.areThereActivitiesInDestination,
        getActivitySuggestions: itineraryStore.getActivitySuggestions,
        getStaticActivitySuggestions:
          itineraryStore.getDestinationSuggestedActivities,
        isSmallDevice: browserStore.isSmallDevice,
        selectedDate: preferredTimeStore.preferredDay,
        getAllCarriers: (ingredientId: IngredientId) =>
          tripState.getAllCarriers(ingredientId).allCarriers,
        isUserFromBookingPage:
          queryParamsStore.queryParams?.customerOrigin ===
          CustomerOriginQueryParamValue.BookingPage,
        hasUserSelectedTransport: hasUserSelectedTransport.value,
        isUserFromStena: isStena(),
      };
      const result = getTripOverviewSegments(tripOverviewSegments);

      return result;
    });

    const isLoading = computed(() => itineraryStore.isSearchingItineraries);
    const isLoadingSimpleTransportSegmentDetails = computed(
      () =>
        mapState.isLoadingCalculatedRoute.value ||
        isFeatureToggleActive('loader')
    );

    const anyItineraryLoaded = computed(() => tripItinerary.value.length > 0);

    const tripItineraryLoader = computed<ItineraryLoaderSegment[]>(() =>
      getTripItineraryLoaderSegments(
        recipeState.recipe.value,
        recipeState.endLocationIngredient.value,
        !!geolocationStore.userGeolocation,
        browserStore.isSmallerThanMediumMin,
        (ingredientId: IngredientId) =>
          tripState.getAllCarriers(ingredientId).allCarriers
      )
    );

    function selectTransport(
      segment:
        | ItineraryNoTransportSegment
        | ItineraryBookableTransportSegment
        | ItinerarySimpleTransportSegment
        | ItineraryTransportLoaderSegment
        | ItineraryNoTransportLoaderSegment
        | ItineraryUpsellNoTransportSegment
        | ItineraryStenaUpsellNoTransportSegment,
      newTransportType: TransportType
    ): void {
      if (
        segment.segmentType === ItinerarySegmentType.Flight &&
        newTransportType !== TransportType.Flight
      ) {
        statsigEventsStore.logTripOverviewFlightRemoved();
      }

      if (
        segment.segmentType === ItinerarySegmentType.Ferry &&
        newTransportType !== TransportType.Ferry
      ) {
        statsigEventsStore.logTripOverviewFerryRemoved();
      }

      const ingredient = getIngredientForSelectTransport(segment);
      const newTransportConnectionType =
        mapTransportTypeToConnectionType(newTransportType);
      const ingredientIndex = recipeState.getIngredientIndex(
        segment.ingredientId
      );
      const isLastTransport =
        ingredientIndex === recipeState.recipe.value.length - 1;

      setEditingIngredient(ingredient);

      if (
        ingredient &&
        segment.segmentType.toLocaleLowerCase() ===
          newTransportType.toLocaleLowerCase()
      ) {
        if (segment.segmentType === ItinerarySegmentType.Flight) {
          routerStore.navigateToFlightsList();
          return;
        }
        if (segment.segmentType === ItinerarySegmentType.Ferry) {
          routerStore.navigateToFerryList();
          return;
        }
        if (
          isItineraryBookableTransport(segment) &&
          isTransportIngredient(ingredient)
        ) {
          editingIngredientStore.setTransportEditingIngredient(
            ingredient,
            'recipe'
          );
        }
        return;
      }

      if (isItineraryBookableTransport(segment)) {
        if (ingredient) {
          recipeState.deleteIngredient(ingredient);
        }
        if (isLastTransport) {
          const endLocationIngredient = recipeState.endLocationIngredient.value;

          setEditingIngredient(endLocationIngredient);
        } else {
          const newIngredientToSetTransport =
            recipeState.recipe.value[ingredientIndex];

          setEditingIngredient(newIngredientToSetTransport);
        }
      }

      if (
        isBookableTransport(newTransportConnectionType) &&
        isBookableTransportType(newTransportType)
      ) {
        recipeState.createTransportIngredient(newTransportConnectionType);

        if (ingredient) {
          ingredient.transportTypeToBeReached = ConnectionType.NONE;
        }
        if (newTransportConnectionType === ConnectionType.FLIGHT) {
          tripOverviewModalsStore.openAddFlightsModal();
        } else {
          tripOverviewModalsStore.openPortSelectionModal(
            editingIngredientStore.editingTransportIngredient?.ingredientId,
            getFromPortInfoFromEditingIngredient(),
            getToPortInfoFromEditingIngredient(),
            newTransportType,
            false
          );
        }
      } else {
        recipeState.setTransportInSettingTransportIngredient(
          newTransportConnectionType
        );
      }
    }

    function getIngredientForSelectTransport(
      segment:
        | ItineraryNoTransportSegment
        | ItineraryBookableTransportSegment
        | ItinerarySimpleTransportSegment
        | ItineraryTransportLoaderSegment
        | ItineraryNoTransportLoaderSegment
        | ItineraryUpsellNoTransportSegment
        | ItineraryStenaUpsellNoTransportSegment
    ): RecipeMainIngredient | undefined {
      let ingredient: RecipeMainIngredient | undefined;
      const canBeLastSegment =
        isItinerarySimpleTransportSegment(segment) ||
        segment.segmentType === ItinerarySegmentType.NoTransport;

      if (canBeLastSegment && segment.isLastSegment) {
        const lastIngredient = recipeState.endLocationIngredient.value;

        ingredient = lastIngredient;
      } else {
        const recipeIngredient = recipeState.getIngredient(
          segment.ingredientId
        );
        if (
          recipeIngredient &&
          (isTransportIngredient(recipeIngredient) ||
            isStayIngredient(recipeIngredient))
        ) {
          ingredient = recipeIngredient;
        }
      }

      return ingredient;
    }

    function setEditingIngredient(
      ingredient: RecipeMainIngredient | undefined
    ): void {
      if (ingredient) {
        recipeState.editIngredientTransport(ingredient);
      }
    }

    function closePortSelectionModal(): void {
      editingIngredientStore.doneEditingIngredient();
      tripOverviewModalsStore.closePortSelectionModal();
    }

    function getAddFlightsModalRoutes(): AddFlightsModalInitialRoute[] {
      const routes: AddFlightsModalInitialRoute[] = [];
      let previousStay: StayIngredient | undefined;
      recipeState.stayOnlyRecipe.value.forEach((currentStay, index) => {
        const currentStayData: AddFlightsModalRouteStopData = {
          displayText: currentStay.displayText,
          searchText: currentStay.searchText,
        };

        const previousStayData: AddFlightsModalRouteStopData | undefined =
          previousStay
            ? {
                displayText: previousStay.displayText,
                searchText: previousStay.searchText,
              }
            : undefined;

        routes.push({
          from: previousStayData,
          to: currentStayData,
          ingredientId: currentStay.ingredientId,
          isLast: false,
        });

        if (index === recipeState.stayOnlyRecipe.value.length - 1) {
          routes.push({
            from: currentStayData,
            to: undefined,
            ingredientId: currentStay.ingredientId,
            isLast: true,
          });
        }

        previousStay = currentStay;
      });

      return routes;
    }

    function confirmAddFlightsModal(routes: AddFligthsModalRoute[]): void {
      tripOverviewModalsStore.closeAddFlightsModal();
      routes.forEach((route) => {
        const ingredient = recipeState.getStayIngredient(route.ingredientId);
        if (!ingredient) {
          return;
        }
        const stayIndex = recipeState.getIngredientIndex(
          ingredient.ingredientId
        );

        const previousIngredient = route.isLast
          ? recipeState.recipe.value[recipeState.recipe.value.length - 1]
          : recipeState.recipe.value[stayIndex - 1];

        const isPreviousIngredientFlight = previousIngredient
          ? isFlightIngredient(previousIngredient)
          : false;

        const isPreviousFerryIngredient = previousIngredient
          ? isFerryIngredient(previousIngredient)
          : false;

        if (!route.isEnabled) {
          if (isPreviousIngredientFlight) {
            if (route.isLast) {
              recipeState.recipe.value.splice(
                recipeState.recipe.value.length - 1,
                1
              );
            } else {
              recipeState.recipe.value.splice(stayIndex - 1, 1);
            }
          }
          return;
        }
        if (route.isLast) {
          const endLocationIngredient = recipeState.endLocationIngredient.value;

          setEditingIngredient(endLocationIngredient);
        } else {
          setEditingIngredient(ingredient);
        }

        recipeState.createTransportIngredient(ConnectionType.FLIGHT);
        const newIngredientId =
          recipeState.settingTransportIngredient.value?.ingredientId;

        if (route.fromAirport.code && route.fromAirport.label) {
          editingIngredientStore.editingFlightIngredient?.setDepartureAirport({
            iata: route.fromAirport.code,
            name: route.fromAirport.name,
            displayingText: route.fromAirport.label,
          });
        }

        if (route.toAirport.code && route.toAirport.label) {
          editingIngredientStore.editingFlightIngredient?.setArrivalAirport({
            iata: route.toAirport.code,
            name: route.toAirport.name,
            displayingText: route.toAirport.label,
          });
        }

        if (editingIngredientStore.editingTransportIngredient) {
          if (newIngredientId) {
            const ingredientIndex =
              recipeState.getIngredientIndex(newIngredientId);
            if (isPreviousIngredientFlight || isPreviousFerryIngredient) {
              if (route.isLast) {
                recipeState.recipe.value[recipeState.recipe.value.length - 1] =
                  editingIngredientStore.editingTransportIngredient;
              } else {
                recipeState.recipe.value[stayIndex - 1] =
                  editingIngredientStore.editingTransportIngredient;
              }
            } else {
              recipeState.addIngredient(
                editingIngredientStore.editingTransportIngredient,
                ingredientIndex
              );
            }

            editingIngredientStore.doneEditingIngredient();
          }
        }
      });
    }

    function showFerryDetailsModal(ingredientId: IngredientId): void {
      const ferryResponse = itineraryStore.getTransportIngredientResponse(
        ingredientId
      ) as AvailableFerryIngredientResponse;
      const ferryStaticInfo = tripState.getTransportStaticInfo(ferryResponse);

      const hasUserSelectedFerryTicketCategory =
        upsellStore.userSelectedFerryTicketCategory?.[
          ferryResponse.addToBasketToken
        ] !== undefined;
      const ticketType = hasUserSelectedFerryTicketCategory
        ? upsellStore.userSelectedFerryTicketCategory?.[
            ferryResponse.addToBasketToken
          ]
        : upsellStore.ferryTicketCategoryDictionary?.[
            ferryResponse.addToBasketToken
          ]?.[0];

      const ferryDetails = getFerryDetails(
        ferryResponse,
        ferryStaticInfo,
        ticketType?.type,
        tripState.ferryVehicleTypes.value
      );
      if (ferryDetails) {
        tripOverviewModalsStore.openFerryDetailsModal(ferryDetails);
      } else {
        throw new Error("Can't open ferry details!");
      }
    }

    function showFlightDetailsModal(ingredientId: IngredientId): void {
      const flightResponse = itineraryStore.getTransportIngredientResponse(
        ingredientId
      ) as AvailableFlightIngredientResponse;

      const flightStaticInfo = tripState.getTransportStaticInfo(flightResponse);

      const flightDetails = getFlightDetails(flightResponse, flightStaticInfo);
      if (flightDetails) {
        tripOverviewModalsStore.openFlightDetailsModal(flightDetails);
      } else {
        throw new Error("Can't open flight details!");
      }
    }

    function loadUserGeolocationToMapState(): void {
      if (geolocationStore.userGeolocation) {
        return;
      }

      geolocationStore.loadUserCoordinates().then(() => {
        const coordinates = geolocationStore.userGeolocation;

        if (coordinates) {
          if (!mapState.startingLocation.value) {
            mapState.setPointLocationFromPosition(
              coordinates,
              MyPositionType.Start
            );
          }

          if (!mapState.endLocation.value) {
            mapState.setPointLocationFromPosition(
              coordinates,
              MyPositionType.End
            );
          }
        }
      });
    }

    function loadDefaultIPUserGeolocationToMapState(): void {
      if (geolocationStore.userGeolocation) {
        return;
      }

      geolocationStore.loadDefaultUserCoordinatesFromIPApi().then(() => {
        const coordinates = geolocationStore.userGeolocation;

        if (coordinates) {
          if (!mapState.startingLocation.value) {
            mapState.setPointLocationFromPosition(
              coordinates,
              MyPositionType.Start
            );
          }

          if (!mapState.endLocation.value) {
            mapState.setPointLocationFromPosition(
              coordinates,
              MyPositionType.End
            );
          }
        }
      });
    }

    function scrollToCalendar(): void {
      const thresholdToPulse = 1000;
      const calendarElement = document.getElementById(
        TripOverviewTemplateIds.PriceCalendar
      );

      assertValue(calendarElement);

      calendarElement.scrollIntoView({
        behavior: 'smooth',
      });

      setTimeout(() => {
        priceCalendarStore.showPulsingIndicator();
      }, thresholdToPulse);
    }

    function getUnavailabilityMessage(
      segmentType: ItinerartBookableSegmentType,
      reasons: UnavailabilityReason[]
    ): string {
      switch (segmentType) {
        case ItinerarySegmentType.Accommodation:
          return getUnavailableAccommodationMessage(reasons);

        case ItinerarySegmentType.Activity:
          return getUnavailableActivityMessage(reasons);

        case ItinerarySegmentType.Flight:
          return getUnavailableFlightMessage(reasons);

        case ItinerarySegmentType.Ferry:
          return getUnavailableFerryMessage(reasons);

        default:
          exhaustiveGuard(
            segmentType,
            `No unavailability message for this segment type: ${segmentType}`
          );
      }
    }

    function changeLocation(
      segment: ItineraryLocationSegment | ItineraryLocationLoaderSegment
    ): void {
      if (segment.segmentType === ItinerarySegmentType.MiddleLocation) {
        tripOverviewModalsStore.openEditTripModal({
          stepType: EditTripStepType.UpdateDestination,
          currentEditingSegment: {
            destinationName: getSearchTextFromLocationSegment(segment.location),
            ingredientId: segment.ingredientId,
            numberOfNights: segment.numberOfNights,
          },
        });
      } else {
        const searchText = getSearchTextFromLocationSegment(segment.location);
        const modalType = getAutocompleteTypeFromLocationSegment(segment);
        tripOverviewModalsStore.openDestinationSelectorModal(
          searchText,
          undefined,
          modalType
        );
      }
    }

    function confirmAlternativeRoomSelection(
      segment: AlternativeRoomSuggestionAccommodationSegment
    ): void {
      statsigEventsStore.logTripOverviewAlternativeStaySelected();

      const heartedHotel: HeartedHotel = {
        code: segment.alternativeSuggestionData.hotelCode,
        isRefundable: segment.alternativeSuggestionData.isRefundable,
        rooms: segment.alternativeSuggestionData.rooms.map((room) => ({
          code: room.roomCode,
          boardBasis: room.mealCode,
        })),
      };

      tripState.heartHotel(heartedHotel, segment.context.ingredientId);
      mapState.mapShouldCenterOnMarkers.value = true;
      editingIngredientStore.doneEditingIngredient();
    }

    function confirmAlternativeTicketSelection(
      segment: AlternativeTicketSuggestionActivitySegment
    ): void {
      statsigEventsStore.logTripOverviewAlternativeActivitySelected();

      const activityIngredient = recipeState.getActivityIngredient(
        segment.context.ingredientId
      );

      activityIngredient.tickets = segment.alternativeSuggestionData.tickets;
    }

    const stopWatch = watch(isLoading, () => {
      const hasLandedInTripOverview =
        routerStore.landingRoute === TripPlannerPage.TripOverview;

      if (!hasLandedInTripOverview) {
        stopWatch();
        return;
      }

      if (!isLoading.value) {
        const isInfiniteLoad = !anyItineraryLoaded.value;

        if (isInfiniteLoad) {
          statsigEventsStore.logTripOverviewInfiniteLoad();
        }

        stopWatch();
      }
    });

    function removeAccommodationFromHotelIngredient(
      hotelIngredientId: IngredientId
    ): void {
      const hotelIngredient = recipeState.getHotelIngredient(hotelIngredientId);

      const destinationPolygonText =
        hotelIngredient.nearbyPolygonText ?? hotelIngredient.commonPolygonText;

      const ownStayIngredient = new OwnStayIngredient(
        destinationPolygonText ?? hotelIngredient.searchText,
        destinationPolygonText ?? hotelIngredient.displayText
      );
      ownStayIngredient.numberOfNights = hotelIngredient.numberOfNights;
      ownStayIngredient.transportTypeToBeReached =
        hotelIngredient.transportTypeToBeReached;
      ownStayIngredient.subIngredients = hotelIngredient.subIngredients.map(
        (activitySubIngredient) => {
          const newActivitySubIngredient = new ActivityIngredient(
            activitySubIngredient.code,
            ownStayIngredient.ingredientId
          );

          newActivitySubIngredient.offset = activitySubIngredient.offset;
          newActivitySubIngredient.tickets = activitySubIngredient.tickets;

          return newActivitySubIngredient;
        }
      );

      recipeState.replaceIngredient(hotelIngredient, ownStayIngredient);
    }

    function addAccommodationToOwnStayIngredient(
      ownStayIngredientId: IngredientId
    ): void {
      const ownStayIngredient =
        recipeState.getOwnStayIngredient(ownStayIngredientId);
      setDestinationTextToOwnStayIngredient(
        ownStayIngredient.ingredientId,
        ownStayIngredient.searchText,
        ownStayIngredient.displayText
      );
    }

    function setDestinationTextToOwnStayIngredient(
      ownStayIngredientId: IngredientId,
      newSearchText: string,
      newDisplayText?: string
    ): void {
      const ownStayIngredient =
        recipeState.getOwnStayIngredient(ownStayIngredientId);

      const hotelIngredient = new HotelIngredient();
      hotelIngredient.setSearchText(newSearchText);
      hotelIngredient.numberOfNights = ownStayIngredient.numberOfNights;
      hotelIngredient.transportTypeToBeReached =
        ownStayIngredient.transportTypeToBeReached;
      hotelIngredient.subIngredients = ownStayIngredient.subIngredients.map(
        (activitySubIngredient) => {
          const newActivitySubIngredient = new ActivityIngredient(
            activitySubIngredient.code,
            hotelIngredient.ingredientId
          );

          newActivitySubIngredient.offset = activitySubIngredient.offset;
          newActivitySubIngredient.tickets = activitySubIngredient.tickets;

          return newActivitySubIngredient;
        }
      );

      if (newDisplayText) {
        hotelIngredient.displayText = newDisplayText;
      }

      recipeState.replaceIngredient(ownStayIngredient, hotelIngredient);
    }

    function updateSuggestionType(
      ingredientId: IngredientId,
      suggestionType: SuggestionType
    ): void {
      const ingredient = recipeState.getIngredient(ingredientId);

      if (isOwnStayIngredient(ingredient) || isActivityIngredient(ingredient)) {
        return;
      }

      const availabilityOrder =
        suggestionType === SuggestionType.MostPopular
          ? AvailabilityOrder.Popular
          : AvailabilityOrder.Cheapest;
      ingredient.availabilityOrder = availabilityOrder;
    }

    return {
      // State
      isFirstTripItineraryRender,

      // Actions
      setUserSelectedTransport,
      initializeView,
      deleteIngredientById,
      changeDestinationNumberOfNights,
      selectTransport,
      closePortSelectionModal,
      getAddFlightsModalRoutes,
      showFerryDetailsModal,
      showFlightDetailsModal,
      confirmAddFlightsModal,
      scrollToCalendar,
      getUnavailabilityMessage,
      changeLocation,
      confirmAlternativeRoomSelection,
      confirmAlternativeTicketSelection,
      confirmAlternativeFlight,
      removeAccommodationFromHotelIngredient,
      addAccommodationToOwnStayIngredient,
      setDestinationTextToOwnStayIngredient,
      updateSuggestionType,
      getFromPortInfoFromEditingIngredient,
      getToPortInfoFromEditingIngredient,
      loadDefaultIPUserGeolocationToMapState,

      // Getters
      hasUserSelectedTransport: readonlyRef(hasUserSelectedTransport),
      isLoading,
      isLoadingSimpleTransportSegmentDetails,
      anyItineraryLoaded,
      hasRecipeIngredients,
      tripItinerary,
      tripItineraryLoader,
      homeFlightData,
      numberOfDestinations,
      numberOfNights,
    };
  }
);
